import React, { useEffect, useState } from "react";
import { Trophy, Crown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function PupOfTheWallCard() {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/pup-of-the-wall").then(({ data }) => setD(data)).catch(() => setD({ current: null, candidates: [] }));
  useEffect(() => { load(); }, []);
  if (!d) return null;

  const crown = async (c, photo) => {
    setBusy(c.review_id);
    try {
      const { data } = await api.post("/admin/pup-of-the-wall", { review_id: c.review_id, photo, note });
      toast.success(data.emailed ? `${c.author_name}'s pup is on the wall — prize code emailed 🏆` : `${c.author_name}'s pup is on the wall 🏆${c.has_email ? " (prize email couldn't be sent)" : " (no email on this review — no prize sent)"}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const clear = async () => { await api.delete("/admin/pup-of-the-wall"); toast.success("Wall feature removed"); load(); };

  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 mb-6" data-testid="pup-of-the-wall-card">
      <h3 className="font-display font-bold text-[#19324A] inline-flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD93D]" /> Pup of the Wall</h3>
      <p className="text-xs text-[#19324A]/50 mt-0.5 mb-4">Each month pick a favourite review photo. It's featured on the homepage and the reviewer gets a {d.percent}% prize code by email.</p>
      {d.current ? (
        <div className="flex items-center gap-3 rounded-2xl bg-[#FFF9EE] p-3 mb-4" data-testid="wall-current">
          <img src={d.current.photo} alt="" className="w-16 h-16 rounded-xl object-cover" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#19324A] truncate">🏆 {d.current.month}: {d.current.author_name}'s pup</p>
            <p className="text-xs text-[#19324A]/55 truncate">{d.current.product?.name}{d.current.prize_code ? ` · prize ${d.current.prize_code}` : " · no prize (no email on review)"}</p>
          </div>
          <button onClick={clear} className="text-xs font-bold text-[#FF6B9D]" data-testid="wall-clear">Remove</button>
        </div>
      ) : <p className="text-sm text-[#19324A]/55 mb-4" data-testid="wall-none">No pup crowned yet this month.</p>}
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Optional caption, e.g. 'Those ears! Winner of October.'" className="w-full rounded-xl border-2 border-[#19324A]/10 px-3 py-2 text-sm outline-none focus:border-[#FFD93D] mb-3" data-testid="wall-note" />
      {d.candidates.length === 0 ? (
        <p className="text-sm text-[#19324A]/55" data-testid="wall-no-candidates">No review photos yet — the "pup pic for 10% off" nudge in the review reminder will fill this up.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="wall-candidates">
          {d.candidates.flatMap((c) => c.photos.map((ph, i) => (
            <button key={`${c.review_id}-${i}`} type="button" disabled={busy === c.review_id} onClick={() => crown(c, ph)} title={`${c.author_name} · ${c.product?.name || ""}`}
              className={`relative aspect-square rounded-xl overflow-hidden group ring-2 ${d.current?.review_id === c.review_id && d.current?.photo === ph ? "ring-[#FFD93D]" : "ring-transparent hover:ring-[#FF6B9D]"}`} data-testid={`wall-candidate-${c.review_id}-${i}`}>
              <img src={ph} alt="" className="w-full h-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 bg-[#19324A]/75 text-white text-[10px] font-bold px-1.5 py-1 truncate text-left">{c.author_name}{c.won_before ? " ★" : ""}</span>
              <span className="absolute inset-0 bg-[#FF6B9D]/80 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-bold gap-1 transition-opacity"><Crown className="w-4 h-4" /> Crown</span>
            </button>
          )))}
        </div>
      )}
    </div>
  );
}
